import { normalizeFieldForBackend } from '../utils/fieldTypeAdapter';

const cloneAndDropUndefined = (value) => {
  if (Array.isArray(value)) return value.map(cloneAndDropUndefined);
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((result, [key, item]) => {
      if (item !== undefined) result[key] = cloneAndDropUndefined(item);
      return result;
    }, {});
  }
  return value;
};

export function sanitizeFormField(field, { itemId, orderId } = {}) {
  const source = normalizeFieldForBackend(field || {});
  const statusOption =
    source.statusOption ??
    (source.is_status ? 'is_status' : source.is_sub_status ? 'is_sub_status' : '');
  const {
    is_status,
    is_sub_status,
    validationError,
    validationErrors,
    _editorId,
    ...contractField
  } = source;

  const sanitized = cloneAndDropUndefined({
    ...contractField,
    statusOption,
    status_parent_key: contractField.status_parent_key ?? '',
    item_id: itemId ?? contractField.item_id,
    order_id: orderId ?? contractField.order_id,
  });

  if (!['DROPDOWN', 'CHECKBOX', 'RADIO'].includes(sanitized.key_type)) {
    delete sanitized.option_colors;
    delete sanitized.key_options;
    sanitized.statusOption = '';
    sanitized.status_parent_key = '';
  }
  return sanitized;
}

export function sanitizeFormFields(fields, { preserveItemIds = false } = {}) {
  const values = Array.isArray(fields) ? fields : Object.values(fields || {});
  const usedIds = new Set();

  return values.filter(Boolean).map((field, index) => {
    let itemId = preserveItemIds ? field.item_id : index + 1;
    if (itemId == null || usedIds.has(String(itemId))) {
      itemId = index + 1;
      while (usedIds.has(String(itemId))) itemId += 1;
    }
    usedIds.add(String(itemId));
    return sanitizeFormField(field, { itemId, orderId: index + 1 });
  });
}

function formMetadata(form) {
  const {
    leads_form_items,
    fields,
    items,
    validationErrors,
    _editorState,
    ...metadata
  } = form || {};
  return cloneAndDropUndefined(metadata);
}

function normalizeUseForAi(value) {
  return value === true || value === 1 || value === '1' ? 1 : 0;
}

export function buildCreateFormPayload(form) {
  const fields = form?.leads_form_items ?? form?.fields ?? form?.items ?? [];
  return {
    ...formMetadata(form),
    use_for_ai: normalizeUseForAi(form?.use_for_ai),
    leads_form_items: sanitizeFormFields(fields),
    global_hot_lead_criteria: cloneAndDropUndefined(form?.global_hot_lead_criteria || []),
    hotness_mapping: cloneAndDropUndefined(form?.hotness_mapping || {}),
    group_options: cloneAndDropUndefined(form?.group_options || []),
    templates: cloneAndDropUndefined(form?.templates || []),
  };
}

export function buildUpdateFormPayload(form) {
  const fields = sanitizeFormFields(
    form?.leads_form_items ?? form?.fields ?? form?.items ?? [],
    { preserveItemIds: true }
  );
  const keyedFields = fields.reduce((result, field) => {
    result[field.item_id] = field;
    return result;
  }, {});

  return {
    ...formMetadata(form),
    use_for_ai: normalizeUseForAi(form?.use_for_ai),
    leads_form_items: keyedFields,
    global_hot_lead_criteria: cloneAndDropUndefined(form?.global_hot_lead_criteria || []),
    hotness_mapping: cloneAndDropUndefined(form?.hotness_mapping || {}),
    group_options: cloneAndDropUndefined(form?.group_options || []),
    templates: cloneAndDropUndefined(form?.templates || []),
  };
}
